import React, { useMemo } from 'react';
import { useSearchInsight } from '../hooks/useSearchInsight';

/**
 * Build the top-3 match contexts to send to the insight API.
 * Uses the full `text` field (not the short snippet) for richer context.
 */
function buildInsightContexts(matches) {
  return [...matches]
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, 3)
    .map((match) => ({
      text: match.metadata?.text || '',
      source_type: match.metadata?.source_type || 'transcript',
    }));
}

/**
 * Renders a single video group pill: insight row + collapsible header + matches.
 *
 * The insight row sits above the collapsible toggle so it is always visible
 * regardless of expanded/collapsed state.
 */
function VideoGroupCard({
  videoGroup,
  groupIndex,
  queryPhrase,
  matchContexts,       // transcript context map from useSearch (for display snippets)
  isCollapsed,
  onToggleCollapse,
  customerKey,
  channelUrl,
  signedToken,
  backendUrl,
  searchDone,
  // style / helper props passed from SearchWidget
  isDark,
  colors,
  fontStack,
  formatTimestamp,
  getDisplayText,
  getActionProps,
  onResultClick,
  getMatchLabel,
}) {
  const insightContexts = useMemo(
    () => buildInsightContexts(videoGroup.matches || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videoGroup.video_id, queryPhrase] // recompute only when the video or query changes
  );

  const { insight, isStreaming } = useSearchInsight({
    videoId: videoGroup.video_id,
    queryPhrase,
    matchContexts: insightContexts,
    customerKey,
    channelUrl,
    signedToken,
    backendUrl,
    enabled: searchDone,
    delayMs: groupIndex * 300,
  });

  const getActionText = (actionProps) => {
    if (actionProps.type === 'pdf') return `Page ${actionProps.pageNumber}`;
    if (actionProps.timestamp) return formatTimestamp(actionProps.timestamp);
    return 'Watch';
  };

  return (
    <div style={{
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      overflow: 'hidden',
      background: colors.card,
    }}>
      {/* AI Insight row — always visible, above collapsible toggle.
          Only rendered once the first token arrives (avoids layout shift). */}
      {insight && (
        <div style={{
          padding: '10px 16px 4px 16px',
          fontSize: '12px',
          color: '#999',
          fontStyle: 'italic',
          background: colors.card,
          minHeight: 0,
          marginBottom: '2px',
        }}>
          {insight}{isStreaming ? ' ▌' : ''}
        </div>
      )}

      {/* Collapsible toggle / video header */}
      <div
        onClick={onToggleCollapse}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px',
          borderBottom: isCollapsed ? 'none' : `1px solid ${colors.border}`,
          background: colors.altRow,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {videoGroup.thumbnail && (
          <img
            src={videoGroup.thumbnail}
            alt=""
            style={{
              width: '96px',
              height: '54px',
              objectFit: 'cover',
              borderRadius: '6px',
              marginRight: '14px',
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            margin: 0,
            fontSize: '0.95rem',
            fontWeight: 600,
            color: colors.text,
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {videoGroup.video_title || `Video: ${videoGroup.video_id}`}
          </h3>
          <span style={{
            fontSize: '0.75rem',
            color: colors.textMuted,
            marginTop: '2px',
            display: 'inline-block',
          }}>
            {videoGroup.matches?.length || 0} match{(videoGroup.matches?.length || 0) !== 1 ? 'es' : ''}
            {videoGroup.publish_date > 0 && (
              <span style={{ marginLeft: '8px' }}>
                &middot; {new Date(videoGroup.publish_date * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            )}
          </span>
        </div>
        <span style={{
          marginLeft: '16px',
          color: isDark ? '#ccc' : '#444',
          fontSize: '1.4rem',
          flexShrink: 0,
          transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
          display: 'inline-block',
          lineHeight: 1,
        }}>
          ▾
        </span>
      </div>

      {/* Collapsible match list */}
      {!isCollapsed && (
        <div>
          {(videoGroup.matches || [])
            .sort((a, b) => (b.score || 0) - (a.score || 0))
            .map((result, index) => {
              const actionProps = getActionProps(result, videoGroup);
              const isLast = index === videoGroup.matches.length - 1;
              const matchLabel = getMatchLabel(result.score || 0);

              return (
                <div
                  key={index}
                  style={{
                    padding: '14px 16px',
                    borderBottom: isLast ? 'none' : `1px solid ${colors.border}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    transition: 'background 0.1s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = colors.altRow; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {result.error ? (
                    <div style={{
                      color: '#dc2626',
                      fontSize: '0.9rem',
                      padding: '12px',
                      background: '#fef2f2',
                      borderRadius: '8px',
                      border: '1px solid #fecaca',
                      flex: 1,
                    }}>
                      {result.error}
                    </div>
                  ) : (
                    <>
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          lineHeight: 1.55,
                          color: colors.text,
                        }}>
                          {(() => {
                            const contextKey = result.metadata?.source_type !== 'pdf' && result.metadata?.start != null
                              ? `${videoGroup.video_id}_${result.metadata.start}`
                              : null;
                            const ctx = contextKey ? matchContexts[contextKey] : null;
                            if (ctx) {
                              return (
                                <>
                                  {ctx.before && (
                                    <span style={{ color: colors.textMuted }}>
                                      ...{ctx.before}{' '}
                                    </span>
                                  )}
                                  <span style={{ fontWeight: 600 }}>
                                    {getDisplayText(result)}
                                  </span>
                                  {ctx.after && (
                                    <span style={{ color: colors.textMuted }}>
                                      {' '}{ctx.after}...
                                    </span>
                                  )}
                                </>
                              );
                            }
                            return getDisplayText(result);
                          })()}
                        </p>
                        <span style={{
                          fontSize: '0.75rem',
                          color: colors.textMuted,
                          marginTop: '4px',
                          display: 'inline-block',
                        }}>
                          {result.metadata?.source_type === 'pdf'
                            ? `${result.metadata.pdf_name}, Page ${result.metadata.page_number}`
                            : result.metadata?.start
                              ? `at ${formatTimestamp(result.metadata.start)}`
                              : 'Video content'
                          }
                        </span>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 7px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          borderRadius: '999px',
                          background: matchLabel.color + '18',
                          color: matchLabel.color,
                          border: `1px solid ${matchLabel.color}40`,
                          marginLeft: '6px',
                          verticalAlign: 'middle',
                        }}>
                          {matchLabel.label}
                        </span>
                      </div>

                      {/* Action */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          onResultClick({ result, videoGroup, actionProps });
                          window.open(actionProps.url, '_blank', 'noopener,noreferrer');
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '6px 14px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          fontFamily: fontStack,
                          color: isDark ? '#e5e5e5' : '#333',
                          background: colors.pillBg,
                          border: `1px solid ${colors.pillBorder}`,
                          borderRadius: '8px',
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          marginTop: '2px',
                          cursor: 'pointer',
                          transition: 'background 0.12s ease',
                        }}
                      >
                        <span>{actionProps.icon}</span>
                        <span>{getActionText(actionProps)}</span>
                      </button>
                    </>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default VideoGroupCard;
